/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2017, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'jquery',
	'doT',
	'underscore',
	'../../../lib/@waca/core-client/js/core-client/ui/core/View',
	'text!../templates/DataGridView.html',
	'../../../app/nls/StringResources',
	'react-dom',
	'../../../lib/@waca/core-client/js/core-client/utils/ClassFactory'

], ($, dot, _, View, template, StringResources, ReactDOM, ClassFactory) => {
	const DataGridView = View.extend({

		/**
		 * The Data grid is an extensible Tabbed view where extensions can plug into
		 * a tab depending on application context.
		 *
		 * Extensions are required to implement the "getTabContrib" function which will return
		 * one or more tab contributions if the provided application context is applicable to the extension.
		 */
		init(options) {
			DataGridView.inherited('init', this, arguments);
			this.glassContext = options.services.biGlass.glassContext;
			this.dashboardApi = options.dashboardApi;
			this.eventRouter = options.eventRouter;
			this.services = options.services;
			this.logger = this.dashboardApi.getGlassCoreSvc('.Logger');
			this.dataSourceCollection = this.dashboardApi.getFeature('dataSources.deprecated').getSourcesCollection();

			this._registerEvents();
			this.gridState = {
				selectedTabId: null,
				tabs: null
			};

			this._loadExtensions(options.appSettings);
			this._resetReady();
			this.renderRequests = [];
		},

		_loadExtensions(appSettings) {
			if (!this._loadExtensionsPromise) {
				const extensionArgs = [
					this.dashboardApi,
					appSettings || {},
					this.eventRouter
				];
				const collectionItems = this.dashboardApi.findGlassCollection('com.ibm.bi.dashboard.dataTray-extensions');

				this._loadExtensionsPromise =  Promise.resolve(collectionItems).then(collectionItems=>{
					let promises = [];
					this.componentControllers = [];
					collectionItems.forEach(item => {
						promises.push(
							ClassFactory.loadModule(item.class)
						);
					});
					return Promise.all(promises).then((moduleList)=>{
						moduleList.forEach((Module)=>{
							this.componentControllers.push(new Module(...extensionArgs));
						});
					});
				});
			}
			return this._loadExtensionsPromise;
		},

		/**
		 * Resets the ready state of the data grid. Invoked when the data grid isn't ready,
		 * for example on initialization or on Hide.
		 */
		_resetReady(){
			this._isReady = false;
			this.viewReady = new Promise(resolve => {
				this._readyView = () => {
					this._isReady = true;
					return this._clearRenderQueue()
						.then(resolve)
						.finally(this._renderReactTabs.bind(this));
				};
			});
		},

		/**
		 * Listen on events for changes in application.
		 */
		_registerEvents() {
			this.registeredEvents = [
				this.eventRouter.on('dataSourcePanel:dataSourceSelected', this._eventHandler, this),
				this.eventRouter.on('tab:tabChanged', this._eventHandler, this),
				this.dashboardApi.getCanvas().on('change:selections', this._eventHandler, this),
				this.eventRouter.on('dataInVis:selected', this._eventHandler, this),
				this.dashboardApi.on('widget:rerendered', this._eventHandler, this),
				this.dashboardApi.on('shapingmodel:changed', this._eventHandler, this),
				this.dataSourceCollection.on('remove', this._eventHandler, this),
				this.on('splitterPanel:show',  this._eventHandler, this),
				this.on('splitterPanel:hide', this._eventHandler, this)
			];
		},

		/**
		 * Event handler to react to changes in the application
		 *
		 * @param {object} event - Event payload
		 * @param {string} eventName - Event name
		 *
		 */
		_eventHandler: function(event, eventName){
			if(eventName === 'splitterPanel:hide'){
				return this._onSplitterHide();
			}

			if(eventName === 'splitterPanel:show'){
				this._onSplitterShow();
			}

			if(!this.renderRequests.length && this._isReady){
				this.renderRequests.push({ event, eventName });
				return this._clearRenderQueue()
					.finally(this._renderReactTabs.bind(this));
			}
			this.renderRequests.push({ event, eventName });
			return Promise.resolve();
		},

		/**
		 * Clear render requests queued when the data grid wasn't ready.
		 */
		_clearRenderQueue(){
			if(!this.renderRequests.length){
				return Promise.resolve();
			}
			const request = this.renderRequests[0];
			return this.renderDataTray(request.event, request.eventName)
				.finally(() => {
					this.renderRequests.shift();
					return this._clearRenderQueue();
				});
		},

		/**
		 * Render the data tray. Extensions are asked for tab contributions. In the
		 * extensions' response to the contribution request, one extension will indicate
		 * it is the active tab and the data grid will focus on it. If no extension indicates
		 * it is the active tab, the first contribution is set to the active tab.
		 *
		 * @param {object} event - Event payload
		 * @param {string} eventName - Event name
		 */
		renderDataTray(event, eventName){
			const contribPromises = [];
			this.componentControllers.forEach((controller) => {
				contribPromises.push(controller.getTabContrib(event, eventName, this.gridState.selectedTabId));
			});

			return Promise.all(contribPromises)
				.then((contribs) => {
					this.gridState.tabs = _.flatten(_.without(contribs, null));
					if(!this.gridState.tabs.length){
						this.gridState.selectedTabId = null;
						this._unmountComponents();
						return Promise.resolve();
					}
					const selectedTabContext = _.findWhere(this.gridState.tabs, { isSelectedOnTrayRender: true }) || this.gridState.tabs[0];
					return selectedTabContext.select(this.gridState.selectedTabId, this.gridContainer)
						.finally(() => this.gridState.selectedTabId = selectedTabContext.id);
				});
		},


		/**
		 * Data grid on show handler
		 */
		_onSplitterShow() {
			this._readyView();
		},

		/**
		 * Data grid on hide handler
		 */
		_onSplitterHide() {
			this._unmountComponents();
			this._resetReady();
		},

		/**
		 * Clean data grid UI components
		 */
		_unmountComponents() {
			if (this._tabsContainer && this.gridContainer) {
				this._unmountReactComponents();
				$(this._tabsContainer).empty();
				$(this.gridContainer).empty();
			}
		},

		/**
		 * Unmount data grid react components
		 */
		_unmountReactComponents() {
			if (this._tabsContainer) {
				ReactDOM.unmountComponentAtNode(this._tabsContainer);
			}
			if (this.gridContainer) {
				ReactDOM.unmountComponentAtNode(this.gridContainer);
			}
		},

		/**
		 * Return splitter necessary information
		 */
		getSplitterOpts() {
			let icons = this.dashboardApi.getFeature('Icons');
			return this.services.getSvc('.StringResources')
				.then(StringResources => ({
					handleClass: 'gridHandle',
					handleIcon: icons.getIcon('table').id,
					hideInConsume: true,
					handleOpenTitle: StringResources.get('dataTrayHandleOpenLabel'),
					handleCloseTitle: StringResources.get('dataTrayHandleCloseLabel'),
					view: this,
					initialSize: 250
				}));
		},

		/**
		 * Remove data grid
		 */
		remove() {
			this._unmountComponents();

			if (this.registeredEvents.length) {
				this.registeredEvents.forEach(event => {
					if (event && event.remove) {
						event.remove();
					}
				});
				this.registeredEvents = [];
			}
			this.gridState.tabs = null;
			this.gridState.selectedTabId = null;
			return DataGridView.inherited('remove', this, arguments);
		},

		/**
		 * Render Grid
		 */
		render() {
			return this._loadExtensionsPromise.then(()=>{
				this.dotTemplate = dot.template(template);
				const sHtml = this.dotTemplate();
				this.$el.html(sHtml).addClass('dashboardGrid');
				this.gridContainer = this.$el.find('.dataGridView')[0];
				this._tabsContainer = this.$el.find('.gridTabs')[0];
				this._readyView();
				return this.viewReady;
			}).catch(e=>{
				this.logger.log(e);
			});
		},

		/**
		 * @returns {String} the id of the currently highlighted tab
		 */
		getHighlightedTabId() {
			return this.gridState.selectedTabId;
		},

		/**
		 * renders tabs from modeling ui
		 * @return Promise
		 */
		_renderReactTabs() {

			if(!this.gridState.tabs || this.gridState.tabs.length === 0){
				return Promise.resolve();
			}

			const props = {
				tabData: this.gridState.tabs,
				onTabChange: this.onTabChange.bind(this),
				selectedTab: this.gridState.selectedTabId
			};
			return new Promise((resolve, reject) => {
				try {
					require(['ca-modeller/shaping'], (Shaping) => {
						if (!this._shapingHelper) {
							this._shapingHelper = new Shaping.Shaping(this.glassContext, {
								featureConfig: {
									calculationAction: true,
									calculationEditAction: true,
									customGroupCreateAction: true,
									customGroupReplaceAction: true
								}
							});
						}
						this._shapingHelper.render(Shaping.GridTabs, props, this._tabsContainer);
						resolve();
					}, reject);
				} catch(error) {
					reject(error);
				}
			});
		},

		/**
		 * Handler for when tab changes
		 */
		onTabChange(id) {
			const tab = _.findWhere(this.gridState.tabs, { id });
			tab.select(this.gridState.selectedTabId, this.gridContainer);
			this.gridState.selectedTabId = tab.id;
			this._renderReactTabs();
		}
	});
	return DataGridView;
});
